import React, { useEffect } from "react";
import { AnimatePresence, motion } from "framer-motion";
import { CheckIcon } from "@heroicons/react/solid";
import { checkPopVariants, tileEntranceVariants } from "@/shared/motion";
import { usePrefersReducedMotion } from "@/hooks/usePrefersReducedMotion";
import { getLayoutWidth } from "@/shared/utils/layoutViewport";

interface IProps {
  isSelected: boolean;
  displayBorders?: boolean;
  onClick?: () => void;
  onDoubleClick?: () => void;
  image: string;
  title: string;
  description?: string;
  videoBannerUrl?: string;
  mobileVideoBannerUrl?: string;
  badge?: string;
  badgeClasses?: string;
  icons?: React.ReactNode;
  heightPx?: number;
  isArcade?: boolean;
  fill?: boolean;
  /**
   * Large-screen only: instead of the fixed 380px height, let the tile grow to
   * fill its (bounded) grid cell, capped at 380px. Used by the root experience
   * selector so tiles shrink on short laptops rather than forcing the scroll
   * container to scroll. Other usages keep the fixed 380px behaviour.
   */
  desktopFill?: boolean;
  /**
   * Renders the title over the video as a large headline (40px, 52px on the two-column
   * layout, 36px on the four-column one) instead of the default 24px. Used by the age group selector, where the title is
   * the only copy on the tile.
   */
  largeTitle?: boolean;
  /**
   * Position in the grid's idle hint, 0-based, which offsets when this tile glows so
   * the hint travels the row. Supplying it opts the tile in; withdraw it (undefined)
   * once the user has engaged, since the hint has done its job by then. Only
   * meaningful on selectable tiles.
   */
  waveIndex?: number;
  /**
   * Called the first time the user reaches this tile with a pointer, the keyboard or a
   * click, so the grid can retire the hint on its sibling tiles too.
   */
  onEngage?: () => void;
  /**
   * Booking Component field keys (`<block type>.<setting id>`) for the copy this
   * tile shows. The theme emits a font rule per configured key, so the same
   * string has to appear on both sides; see snippets/booking-font-fields-css.
   */
  titleFieldKey?: string;
  descriptionFieldKey?: string;
}

// Black outline on the centred tile title so it stays legible over bright video frames.
// The stroke is sized in em so it scales with the large titles, and `paint-order` draws it
// behind the fill so only the outer half shows and the glyphs keep their full weight.
const TITLE_OUTLINE_STYLE: React.CSSProperties = {
  WebkitTextStroke: "0.1em #000",
  paintOrder: "stroke fill",
};

const HYPERKARTING_GRADIENT ="linear-gradient(282.87deg,#f908ff 6.14%,#d32efe 14.09%,#37ccfb 91.49%,#05fffa 103.26%)";
const BATTLEKART_GRADIENT = "linear-gradient(180deg, #FAC618 0%, #EA581A 100%)";

const TrackTile: React.FC<IProps> = ({
  isSelected,
  onClick,
  image,
  title,
  displayBorders,
  onDoubleClick,
  description,
  videoBannerUrl,
  mobileVideoBannerUrl,
  badge,
  heightPx,
  icons,
  badgeClasses,
  isArcade,
  fill,
  desktopFill,
  largeTitle,
  waveIndex,
  onEngage,
  titleFieldKey,
  descriptionFieldKey,
}) => {
  const prefersReducedMotion = usePrefersReducedMotion();
  const [isLargeScreen, setIsLargeScreen] = React.useState(false);

  useEffect(() => {
    const checkScreenSize = () => {
      setIsLargeScreen(getLayoutWidth() >= 1024);
    };

    checkScreenSize();
    window.addEventListener("resize", checkScreenSize);
    return () => window.removeEventListener("resize", checkScreenSize);
  }, []);

  // When `fill` is enabled the tile stretches to use the full available width instead of
  // the capped 280px (see the width class below). `useMobileFill` additionally governs
  // the below-desktop layout tweaks (content centering, flex growth).
  const useMobileFill = fill && !isLargeScreen;
  // If no explicit height is supplied, additionally grow to fill the column height
  // (flex-1) — used by the vertically-stacked experience selector so there's no gap
  // above the fixed bottom nav. In a grid (e.g. age groups) an explicit heightPx is
  // provided, so we keep that height and only widen the tile.
  const useFlexFill = useMobileFill && heightPx === undefined;

  // NOTE: when the tile is not filling we keep `lg:tw-mx-auto` to centre the fixed 280px
  // tile in its grid column. When filling we must NOT rely on auto margins — auto margins
  // disable grid `stretch`, which would collapse the tile to its content's intrinsic
  // (video/image) width and produce uneven tiles. Instead we force an explicit full width.
  // desktopFill tiles top-align within their (bounded) grid row so the spacing above
  // them stays exactly as designed; any leftover room collects below, as before.
  const lgLayoutClass = desktopFill ? "lg:tw-flex lg:tw-flex-col lg:tw-justify-start lg:tw-h-full lg:tw-min-h-0" : "lg:tw-block";
  const containerClass = `hover:tw-cursor-pointer tw-flex tw-flex-col tw-justify-center tw-items-center ${lgLayoutClass} ${
    fill ? "tw-w-full" : "lg:tw-mx-auto"
  } tw-px-[10px] sm:tw-px-[0] ${useFlexFill ? "tw-flex-1 tw-min-h-0" : ""}`;

  const useMobileAspectRatio = !isLargeScreen && !useMobileFill && heightPx === undefined;

  const tileStyle: React.CSSProperties = {
    padding: "4px",
    background: "transparent",
    ...(isLargeScreen
      ? desktopFill
        ? { maxHeight: "380px" }
        : { height: "380px" }
      : useFlexFill
        ? { height: "100%" }
        : useMobileAspectRatio
          ? {}
          : { height: `${heightPx ?? (isArcade ? 120 : 160)}px` }),
  };

  const mobileAspectClass = useMobileAspectRatio ? (isArcade ? "tw-aspect-[8/3]" : "tw-aspect-[2/1]") : "";

  const activeVideoUrl = !isLargeScreen && mobileVideoBannerUrl ? mobileVideoBannerUrl : videoBannerUrl;

  const titleText = title.split("_").join(" ");
  // Tile titles are headings, so the ALL CAPS treatment comes from the brand
  // heading token rather than from the string. Match on a lowercased copy so the
  // displayed text keeps its original casing if that token is ever set to `none`.
  const titleMatchKey = titleText.toLowerCase();
  // xl steps back down because the selector goes to four ~250px columns there, which is
  // too narrow for the widest title ("Ages 10-14") on one line at 52px.
  const titleSizeClass = largeTitle ? "tw-text-[40px] lg:tw-text-[52px] xl:tw-text-[36px]" : "tw-text-[24px]";
  const showSelectedIndicator = isSelected || displayBorders;
  const isBattleKartTile = titleMatchKey.includes("battle");
  // Description is anchored absolutely to the bottom of the tile; padding reserves the
  // area for it inside the tile so the title never collides with it.
  const reserveDescriptionSlot = !!description;
  // Only push content down from the badge corner when there is a description below
  // the title. For title-only tiles we want the title fully centred even with a badge.
  // 38px gives breathing room below the (widest) "Intermediate Track" badge.
  const badgeTopPaddingClass = badge && reserveDescriptionSlot ? "tw-pt-[38px] lg:tw-pt-0" : "lg:tw-pt-0";
  // Description pill sits at `bottom-[8px]` and is ~20px tall, so 30px of bottom
  // padding keeps the title clear of it.
  const descriptionAreaPaddingClass = reserveDescriptionSlot ? "tw-pb-[30px] lg:tw-pb-0" : "lg:tw-pb-0";

  const brandGradient = isBattleKartTile ? BATTLEKART_GRADIENT : HYPERKARTING_GRADIENT;
  // Only the selector screens hand the tile a click handler. The review/upsell banners
  // pass `displayBorders` for a static "this is your booking" tile, so they must not
  // pick up the hover affordance or the selection ring.
  const isInteractive = !!onClick;
  // Hover, press and the selection ring are driven by the .track-tile rules in App.css
  // rather than React state, so the browser owns the hover lifecycle (no ring left lit
  // when the pointer leaves mid-render) and touch devices never latch it.
  // The hint only speaks to someone who hasn't found the tiles yet, so the first
  // pointer or keyboard contact retires it here rather than in CSS: a hover rule that
  // suppresses the animation hands it straight back when the pointer leaves, and the
  // tile carries on glowing under nothing. Retiring it in state is one-way, and the
  // tile also tells the grid so the siblings stop hinting at the same moment.
  const [isHintRetired, setIsHintRetired] = React.useState(false);
  const retireHint = React.useCallback(() => {
    setIsHintRetired(true);
    onEngage?.();
  }, [onEngage]);

  const isWaving = isInteractive && waveIndex !== undefined && !isHintRetired;
  const tileStateClass = `track-tile ${isInteractive ? "track-tile--interactive" : ""} ${isInteractive && isSelected ? "track-tile--selected" : ""} ${
    isWaving ? "track-tile--waving" : ""
  }`;
  // Offsets this tile's turn in the idle hint; consumed by animation-delay in App.css.
  const waveStyle = isWaving ? ({ "--track-tile-wave-index": waveIndex } as React.CSSProperties) : undefined;

  // The static review/upsell banners keep the original heavy grey wash. A selected
  // selector tile gets a lighter scrim instead: the ring and check badge already carry
  // the selection, so the chosen tile should stay vivid rather than grey out — it was
  // reading as the most muted tile on screen precisely when it was the active one.
  const videoOverlayClass = displayBorders
    ? "tw-bg-[#434343] tw-bg-opacity-90"
    : isSelected
      ? "tw-bg-black tw-bg-opacity-45"
      : "tw-bg-opacity-50";
  const imageOverlayClass = displayBorders
    ? "tw-bg-[#434343] tw-bg-opacity-90"
    : isSelected
      ? "tw-bg-indigo-900 tw-bg-opacity-70"
      : "tw-bg-indigo-900 tw-bg-opacity-50";

  // Selectable tiles are divs, so they need the button semantics spelled out: without
  // these they are invisible to keyboard and assistive tech (the static review/upsell
  // banners stay plain, since they aren't actionable).
  const interactiveProps: React.HTMLAttributes<HTMLDivElement> = isInteractive
    ? {
        role: "button",
        tabIndex: 0,
        "aria-pressed": isSelected,
        onKeyDown: (event) => {
          if (event.key !== "Enter" && event.key !== " ") return;
          event.preventDefault();
          onClick?.();
        },
        // Tabbing to a tile counts as finding it just as much as pointing at it does,
        // so keyboard users don't leave a row of tiles pulsing behind the focus ring.
        ...(isWaving ? { onPointerEnter: retireHint, onFocus: retireHint } : {}),
      }
    : {};

  return (
    <motion.div className={containerClass} variants={tileEntranceVariants}>
      <div
        className={`${tileStateClass} tw-w-full ${fill ? "" : "sm:tw-w-[280px]"} ${
          desktopFill ? "lg:tw-flex-1 lg:tw-min-h-0" : ""
        } tw-rounded-[25px] tw-overflow-hidden tw-relative ${mobileAspectClass}`}
        style={{ ...tileStyle, ...waveStyle }}
        onClick={onClick}
        onDoubleClick={onDoubleClick}
        {...interactiveProps}
      >
        {/* Fills the tile box's 4px of padding, so it reads as a gradient ring around the media. */}
        <span aria-hidden className="track-tile__ring tw-absolute tw-inset-0 tw-z-0 tw-rounded-[25px] tw-pointer-events-none" style={{ background: brandGradient }} />
        <AnimatePresence>
          {showSelectedIndicator && (
            <motion.div
              key="selected-check"
              className="tw-absolute tw-bottom-[10px] tw-right-[10px] tw-z-10 tw-flex tw-h-[34px] tw-w-[34px] tw-items-center tw-justify-center tw-rounded-full tw-shadow-[0_2px_8px_rgba(0,0,0,0.45)]"
              style={{ background: brandGradient }}
              aria-label="Selected"
              variants={checkPopVariants}
              initial={prefersReducedMotion ? false : "initial"}
              animate="animate"
              exit={prefersReducedMotion ? undefined : "exit"}
            >
              <CheckIcon className="tw-w-[22px] tw-h-[22px] tw-text-white" />
            </motion.div>
          )}
        </AnimatePresence>
        {activeVideoUrl ? (
          <div className="tw-h-full tw-w-full tw-rounded-[22px] tw-relative tw-z-[1] tw-overflow-hidden">
            <video
              key={activeVideoUrl}
              className="track-tile__media tw-h-full tw-w-full tw-rounded-[22px] tw-object-cover"
              src={activeVideoUrl}
              autoPlay
              loop
              muted
              playsInline
              controls={false}
              style={{ pointerEvents: "none" }}
            />
            {badge && (
              <div
                className={`tw-absolute tw-top-[10px] tw-right-[10px] tw-z-10 tw-text-white tw-text-[11px] lg:tw-text-[15px] tw-font-bold tw-rounded-[20px] lg:tw-rounded-[33px] tw-px-[10px] lg:tw-px-[15px] tw-py-[3px] lg:tw-py-[5px] ${badgeClasses}`}
                style={{ background: isBattleKartTile && isSelected ? BATTLEKART_GRADIENT : HYPERKARTING_GRADIENT }}
              >
                <div className="tw-flex tw-items-center tw-gap-1 lg:tw-gap-2">
                  {icons}
                  {badge}
                </div>
              </div>
            )}
            <div
              className={`tw-flex tw-flex-col tw-items-center tw-justify-center tw-gap-[4px] tw-h-full tw-w-full tw-absolute tw-top-0 tw-left-0 tw-rounded-[22px] tw-px-[10px] tw-transition-[background-color,opacity] tw-duration-300 tw-ease-[cubic-bezier(0.4,0,0.2,1)] ${videoOverlayClass} ${badgeTopPaddingClass} ${descriptionAreaPaddingClass}`}
            >
              {isBattleKartTile ? (
                <span data-hlc-field={titleFieldKey} style={TITLE_OUTLINE_STYLE} className="booking-heading tw-text-white tw-font-bold hover:tw-cursor-text tw-text-[24px] tw-leading-[1.1]">
                  BattleKart
                </span>
              ) : (
                <span data-hlc-field={titleFieldKey} style={TITLE_OUTLINE_STYLE} className={`booking-heading tw-text-white tw-font-bold hover:tw-cursor-text ${titleSizeClass} tw-leading-[1.1]`}>
                  {titleText}
                </span>
              )}
            </div>
            {reserveDescriptionSlot && (
              <p
                data-hlc-field={descriptionFieldKey}
                className="tw-text-white tw-absolute tw-bottom-[8px] tw-left-1/2 tw-transform tw--translate-x-1/2 tw-z-10 tw-bg-[#374151b8] tw-px-[12px] tw-py-[4px] tw-text-center tw-text-[10px] tw-leading-[1.2] tw-rounded-[14px] tw-font-[600] tw-whitespace-nowrap lg:tw-hidden tw-m-0"
              >
                {description}
              </p>
            )}
          </div>
        ) : (
          <div
            className="tw-bg-cover tw-bg-center tw-h-full tw-w-full tw-rounded-[22px] tw-relative tw-z-[1] tw-overflow-hidden"
            style={{ backgroundImage: `url("https://hyperkarting-product-component.vercel.app${image}")` }}
          >
            <div
              className={`tw-flex tw-items-center tw-justify-center tw-h-full tw-w-full tw-relative tw-rounded-[22px] tw-transition-[background-color] tw-duration-300 tw-ease-[cubic-bezier(0.4,0,0.2,1)] ${imageOverlayClass} ${
                badge ? "tw-pt-[36px] lg:tw-pt-0" : ""
              }`}
            >
              {badge && (
                <div
                  className="tw-absolute tw-top-[10px] tw-right-[10px] tw-text-white tw-text-[11px] lg:tw-text-[14px] tw-font-bold tw-rounded-[16px] lg:tw-rounded-[22px] tw-px-[8px] lg:tw-px-[10px] tw-py-[3px] lg:tw-py-[5px]"
                  style={{ background: HYPERKARTING_GRADIENT }}
                >
                  {badge}
                </div>
              )}
              <span data-hlc-field={titleFieldKey} style={TITLE_OUTLINE_STYLE} className="booking-heading tw-text-white tw-font-bold hover:tw-cursor-text tw-text-[24px]">
                {titleText}
              </span>
            </div>
          </div>
        )}
      </div>
      {description && (
        <p
          data-hlc-field={descriptionFieldKey}
          className={`tw-text-white ${fill ? "tw-w-full" : "tw-w-[280px]"} tw-text-center tw-pt-[10px] tw-hidden lg:tw-block`}
        >
          {description}
        </p>
      )}
    </motion.div>
  );
};

export default TrackTile;
